import type { AdminRole, AdminSurfaceConfig } from '../surface';

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface AdminProfile {
  id: string;
  role: AdminRole;
  nickname: string | null;
  internal_login_id: string | null;
  phone_number: string | null;
  status: 'active' | 'suspended' | string;
}

export const ADMIN_PASSWORD_MIN_LENGTH = 12;

export interface PasswordChangeInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function normalizeCredentialIdentifier(identifier: string, surface: AdminSurfaceConfig): string {
  const value = identifier.trim();

  if (value.includes('@')) {
    return value;
  }

  return `${value}@octopus.internal`;
}

export function isAllowedAdminRole(role: string | null | undefined, surface: AdminSurfaceConfig): role is AdminRole {
  return role === surface.allowedRole;
}

export function isActiveAdminProfile(
  profile: AdminProfile | null | undefined,
  surface: AdminSurfaceConfig
): profile is AdminProfile {
  return !!profile && profile.status === 'active' && isAllowedAdminRole(profile.role, surface);
}

export function getDisplayName(profile: AdminProfile | null, user: AuthUser | null): string {
  if (profile?.nickname) {
    return profile.nickname;
  }

  if (profile?.internal_login_id) {
    return profile.internal_login_id;
  }

  return user?.email ?? '관리자';
}

export function validatePasswordChangeInput(input: PasswordChangeInput): string | null {
  if (!input.currentPassword) {
    return '현재 비밀번호를 입력하세요.';
  }

  if (input.newPassword.length < ADMIN_PASSWORD_MIN_LENGTH) {
    return `새 비밀번호는 ${ADMIN_PASSWORD_MIN_LENGTH}자 이상이어야 합니다.`;
  }

  if (input.newPassword === input.currentPassword) {
    return '새 비밀번호는 현재 비밀번호와 달라야 합니다.';
  }

  if (input.newPassword !== input.confirmPassword) {
    return '새 비밀번호 확인이 일치하지 않습니다.';
  }

  return null;
}
